import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import ReportMultiSelect from './ReportMultiSelect';

const INPUT_BASE =
  'w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

const SECTION_HEADER = 'text-xs font-bold tracking-wide uppercase text-slate-500 border-b border-gray-200 pb-2';
const FIELD_LABEL = 'block text-sm font-medium text-gray-700 mb-1';
const HINT = 'text-xs text-gray-500 mt-1.5';

// Per-period coverage form. Mirrors hydrateCoverageForm() in SystemDetail.js so
// the two pages share the same shape — and on save we hit the same
// /api/systems/:id/coverage/:year endpoint.
function emptyCoverageForm() {
  return {
    keySystem: false,
    socReliance: false,
    soc1: false,
    soc1Type1: false,
    soc1Type1Periods: '',
    soc1Type2: false,
    soc1Type2Periods: '',
    soc2: false,
    soc2Type1: false,
    soc2Type1Periods: '',
    soc2Type2: false,
    soc2Type2Periods: '',
    bridgeLetter: false,
    bridgeLetterPeriod: '',
  };
}

// Year picker options: current calendar year + three prior years.
// `kind` annotates each option for the dropdown label.
function buildYearOptions() {
  const current = new Date().getFullYear();
  return [
    { year: current, kind: 'current' },
    { year: current - 1, kind: 'past' },
    { year: current - 2, kind: 'past' },
    { year: current - 3, kind: 'past' },
  ];
}

// Returns true when the form has any non-default value, so we know whether
// it's worth issuing the coverage PUT on save.
function coverageHasValues(cov) {
  return Boolean(
    cov.keySystem ||
    cov.socReliance ||
    cov.soc1 || cov.soc2 ||
    cov.soc1Type1 || cov.soc1Type2 ||
    cov.soc2Type1 || cov.soc2Type2 ||
    cov.bridgeLetter ||
    cov.soc1Type1Periods.trim() ||
    cov.soc1Type2Periods.trim() ||
    cov.soc2Type1Periods.trim() ||
    cov.soc2Type2Periods.trim() ||
    cov.bridgeLetterPeriod.trim()
  );
}

function SystemCreate() {
  const navigate = useNavigate();
  const location = useLocation();

  // System-level form state.
  const [systemId, setSystemId] = useState('');
  const [name, setName] = useState('');
  const [owner, setOwner] = useState('');
  const [rationale, setRationale] = useState('');
  // Linked reports — array of report UUIDs. Replaces the legacy free-text
  // "Reports & artifacts (IPE)" textarea; writes the system_reports
  // junction via the new POST /api/systems body.reportIds field.
  const [reportIds, setReportIds] = useState([]);
  const [isActive, setIsActive] = useState(true);

  // Per-period coverage state. selectedYear drives which year's coverage row
  // we'll write on save; the user can pick a year before saving.
  const yearOptions = useMemo(buildYearOptions, []);
  const [selectedYear, setSelectedYear] = useState(yearOptions[0].year);
  const [coverageForm, setCoverageForm] = useState(emptyCoverageForm());

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = useMemo(
    () => systemId.trim().length > 0 && name.trim().length > 0 && !submitting,
    [systemId, name, submitting]
  );

  const handleSave = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // Step 1: create the canonical system row. Backend assigns the UUID;
      // the response carries it back so we can attach coverage to it.
      const systemPayload = {
        systemId: systemId.trim(),
        name: name.trim(),
        systemOwner: owner.trim(),
        // Mirror the period's scope flag onto the parent system row so the
        // listing's Key chip and the catalog default match the period the
        // user just edited.
        keySystem: coverageForm.keySystem,
        socReport: coverageForm.socReliance,
        categorization: [],
        rationale: rationale.trim(),
        ipe: [],
        reportIds,
        isActive,
      };
      const createRes = await axiosInstance.post('/api/systems', systemPayload);
      const created = createRes.data || {};
      const newId = created.id;

      // Step 2: write per-period coverage if the user filled any of it.
      // Mirrors SystemDetail's save shape, including the sub-field gating —
      // SOC1/SOC2 sub-flags and periods only count when the parent toggle
      // is on, so an unticked toggle authoritatively clears nested state.
      if (newId && coverageHasValues(coverageForm)) {
        const coveragePayload = {
          keySystem: coverageForm.keySystem,
          socReliance: coverageForm.socReliance,
          soc1: coverageForm.socReliance && coverageForm.soc1,
          soc1Type1: coverageForm.socReliance && coverageForm.soc1 && coverageForm.soc1Type1,
          soc1Type1Periods:
            coverageForm.socReliance && coverageForm.soc1 && coverageForm.soc1Type1
              ? coverageForm.soc1Type1Periods.trim()
              : '',
          soc1Type2: coverageForm.socReliance && coverageForm.soc1 && coverageForm.soc1Type2,
          soc1Type2Periods:
            coverageForm.socReliance && coverageForm.soc1 && coverageForm.soc1Type2
              ? coverageForm.soc1Type2Periods.trim()
              : '',
          soc2: coverageForm.socReliance && coverageForm.soc2,
          soc2Type1: coverageForm.socReliance && coverageForm.soc2 && coverageForm.soc2Type1,
          soc2Type1Periods:
            coverageForm.socReliance && coverageForm.soc2 && coverageForm.soc2Type1
              ? coverageForm.soc2Type1Periods.trim()
              : '',
          soc2Type2: coverageForm.socReliance && coverageForm.soc2 && coverageForm.soc2Type2,
          soc2Type2Periods:
            coverageForm.socReliance && coverageForm.soc2 && coverageForm.soc2Type2
              ? coverageForm.soc2Type2Periods.trim()
              : '',
          bridgeLetter: coverageForm.bridgeLetter,
          bridgeLetterPeriod: coverageForm.bridgeLetter ? coverageForm.bridgeLetterPeriod.trim() : '',
        };
        try {
          await axiosInstance.put(`/api/systems/${newId}/coverage/${selectedYear}`, coveragePayload);
        } catch (covErr) {
          // The system was created; surface a soft warning but still nav back.
          // Edge case the user can fix on the detail page.
          console.warn('Coverage save after system create failed:', covErr);
        }
      }

      navigate('/sox-systems');
    } catch (err) {
      console.error('Create system failed:', err);
      setError(resolve403ErrorMessage(err, err?.response?.data?.error || 'Failed to create system'));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create system</h1>
            <p className="text-sm text-gray-600 mt-1 max-w-xl">
              Add a system to the catalog. Fields and layout match the edit page so the catalog stays
              consistent whether a row is created manually or auto-backfilled from a flowchart.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/sox-systems')}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
          >
            Cancel
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* Summary card — matches SystemDetail.js. */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-5 space-y-4">
            <h2 className={SECTION_HEADER}>Summary</h2>

            <div>
              <label className={FIELD_LABEL}>
                System code <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                placeholder="Short code (unique in your org)"
                autoComplete="off"
                value={systemId}
                onChange={(e) => setSystemId(e.target.value)}
                className={INPUT_BASE}
              />
              <p className={HINT}>Shown in matrices and filters — the same value people use in working papers.</p>
            </div>

            <div>
              <label className={FIELD_LABEL}>
                Display name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. SAP ECC (FI/CO)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={INPUT_BASE}
              />
            </div>

            <div>
              <label className={FIELD_LABEL}>System owner</label>
              <input
                type="text"
                placeholder="e.g. Jamie Chen · Finance Ops"
                autoComplete="name"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                className={INPUT_BASE}
              />
            </div>

            <div>
              <label className={FIELD_LABEL}>Reason for in scope</label>
              <textarea
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                className={`${INPUT_BASE} min-h-[88px] resize-y`}
                placeholder="Plain-language reason this system is in the inventory for the period…"
              />
            </div>
          </div>

          {/* Coverage & evidence — same per-period editor as SystemDetail.js. */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-5 space-y-5">
            <h2 className={SECTION_HEADER}>Coverage &amp; evidence</h2>

            <div>
              <label className={FIELD_LABEL}>Period</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className={`${INPUT_BASE} max-w-xs`}
              >
                {yearOptions.map((opt) => (
                  <option key={opt.year} value={opt.year}>
                    {opt.year} ({opt.kind})
                  </option>
                ))}
              </select>
              <p className={HINT}>The coverage block below is saved against this year. You can edit other years on the system detail page after creation.</p>
            </div>

            <div>
              <label className={FIELD_LABEL}>Scope</label>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="coverage-scope"
                    checked={coverageForm.keySystem}
                    onChange={() => setCoverageForm({ ...coverageForm, keySystem: true })}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  Key
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="coverage-scope"
                    checked={!coverageForm.keySystem}
                    onChange={() => setCoverageForm({ ...coverageForm, keySystem: false })}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  Non-Key
                </label>
              </div>
            </div>

            <div>
              <label className={FIELD_LABEL}>SOC reliance</label>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="coverage-soc-reliance"
                    checked={coverageForm.socReliance}
                    onChange={() => setCoverageForm({ ...coverageForm, socReliance: true })}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  Yes
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="coverage-soc-reliance"
                    checked={!coverageForm.socReliance}
                    onChange={() => setCoverageForm({ ...coverageForm, socReliance: false })}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  No
                </label>
              </div>
            </div>

            {coverageForm.socReliance && (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                {/* SOC 1 */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={coverageForm.soc1}
                      onChange={(e) => setCoverageForm({ ...coverageForm, soc1: e.target.checked })}
                      className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                    />
                    SOC&nbsp;1
                  </label>
                  {coverageForm.soc1 && (
                    <div className="mt-2 ml-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mb-1">
                          <input
                            type="checkbox"
                            checked={coverageForm.soc1Type1}
                            onChange={(e) => setCoverageForm({ ...coverageForm, soc1Type1: e.target.checked })}
                            className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                          />
                          Type&nbsp;I
                        </label>
                        {coverageForm.soc1Type1 && (
                          <input
                            type="text"
                            value={coverageForm.soc1Type1Periods}
                            onChange={(e) => setCoverageForm({ ...coverageForm, soc1Type1Periods: e.target.value })}
                            placeholder="Periods, comma-separated (e.g. Q1 2025, Q2 2025)"
                            className={INPUT_BASE}
                          />
                        )}
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mb-1">
                          <input
                            type="checkbox"
                            checked={coverageForm.soc1Type2}
                            onChange={(e) => setCoverageForm({ ...coverageForm, soc1Type2: e.target.checked })}
                            className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                          />
                          Type&nbsp;II
                        </label>
                        {coverageForm.soc1Type2 && (
                          <input
                            type="text"
                            value={coverageForm.soc1Type2Periods}
                            onChange={(e) => setCoverageForm({ ...coverageForm, soc1Type2Periods: e.target.value })}
                            placeholder="Periods, comma-separated"
                            className={INPUT_BASE}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* SOC 2 */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={coverageForm.soc2}
                      onChange={(e) => setCoverageForm({ ...coverageForm, soc2: e.target.checked })}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    SOC&nbsp;2
                  </label>
                  {coverageForm.soc2 && (
                    <div className="mt-2 ml-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mb-1">
                          <input
                            type="checkbox"
                            checked={coverageForm.soc2Type1}
                            onChange={(e) => setCoverageForm({ ...coverageForm, soc2Type1: e.target.checked })}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          Type&nbsp;I
                        </label>
                        {coverageForm.soc2Type1 && (
                          <input
                            type="text"
                            value={coverageForm.soc2Type1Periods}
                            onChange={(e) => setCoverageForm({ ...coverageForm, soc2Type1Periods: e.target.value })}
                            placeholder="Periods, comma-separated"
                            className={INPUT_BASE}
                          />
                        )}
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mb-1">
                          <input
                            type="checkbox"
                            checked={coverageForm.soc2Type2}
                            onChange={(e) => setCoverageForm({ ...coverageForm, soc2Type2: e.target.checked })}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          Type&nbsp;II
                        </label>
                        {coverageForm.soc2Type2 && (
                          <input
                            type="text"
                            value={coverageForm.soc2Type2Periods}
                            onChange={(e) => setCoverageForm({ ...coverageForm, soc2Type2Periods: e.target.value })}
                            placeholder="Periods, comma-separated"
                            className={INPUT_BASE}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className={FIELD_LABEL}>Bridge letter</label>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="coverage-bridge-letter"
                    checked={coverageForm.bridgeLetter}
                    onChange={() => setCoverageForm({ ...coverageForm, bridgeLetter: true })}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  Yes
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="coverage-bridge-letter"
                    checked={!coverageForm.bridgeLetter}
                    onChange={() => setCoverageForm({ ...coverageForm, bridgeLetter: false })}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  No
                </label>
              </div>
              {coverageForm.bridgeLetter && (
                <input
                  type="text"
                  value={coverageForm.bridgeLetterPeriod}
                  onChange={(e) => setCoverageForm({ ...coverageForm, bridgeLetterPeriod: e.target.value })}
                  placeholder="Period (e.g. Aug–Dec 2025)"
                  className={INPUT_BASE}
                />
              )}
            </div>

            <div>
              <label className={FIELD_LABEL}>Active in catalog</label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                Active
              </label>
            </div>

            <div>
              <label className={FIELD_LABEL}>Reports &amp; artifacts (IPE)</label>
              <ReportMultiSelect
                value={reportIds}
                onChange={setReportIds}
                returnPath={`${location.pathname}${location.search || ''}`}
              />
              <p className={HINT}>
                Pick reports from the catalog — or use “Add new report” to create one,
                which links it back to this system automatically.
              </p>
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => navigate('/sox-systems')}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:border-blue-300 disabled:cursor-not-allowed"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? 'Saving…' : 'Save system'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SystemCreate;
